import React, { useState, useEffect, useCallback } from 'react';
import { LaTeXRenderer, MathJaxRenderer } from '../math-renderers';
import { flagQuestion } from '../apiClient';
import './SolutionPopup.css';

// Solution interfaces based on the API response structure
interface SolutionStep {
  stepNumber: number;
  title: string;
  explanation: string;
  formula?: string;
  calculation?: string | string[];
  result?: string;
}

interface SolutionData {
  questionText: string;
  questionType: string;
  subject: string;
  topic: string;
  difficulty: string;
  givenInformation: string[];
  conceptsUsed: string[];
  solutionSteps: SolutionStep[];
  finalAnswer: string;
  keyInsights: string[];
  commonMistakes: string[];
  alternativeApproaches: string[];
  confidence: number;
  estimatedTimeToSolve: string;
  studyTips: string;
  metadata: {
    solution_timestamp: string;
    model_used: string;
    image_url: string;
    correct_option_provided: boolean;
    has_additional_context: boolean;
  };
}

interface SolutionPopupProps {
  isOpen: boolean;
  onClose: () => void;
  questionId: number;
  questionImageUrl?: string;
  fetchQuestionSolution: (questionId: number) => Promise<any>;
}

const SolutionPopup: React.FC<SolutionPopupProps> = ({
  isOpen,
  onClose,
  questionId,
  questionImageUrl,
  fetchQuestionSolution
}) => {
  const [solution, setSolution] = useState<SolutionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useMathJax, setUseMathJax] = useState(true); // Default to MathJax
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagging, setFlagging] = useState(false);
  const [flagSuccess, setFlagSuccess] = useState(false);
  
  const fetchSolution = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSolution(null);
    
    try {
      const response = await fetchQuestionSolution(questionId);
      
      if (response && response.status === 'success' && response.result?.solution) {
        // Parse the solution JSON string
        const solutionData = typeof response.result.solution === 'string' 
          ? JSON.parse(response.result.solution) 
          : response.result.solution;
        
        setSolution(solutionData);
      } else {
        setError('No solution available for this question');
      }
    } catch (err) {
      console.error('Error fetching solution:', err);
      setError('Failed to load solution');
    } finally {
      setLoading(false);
    }
  }, [questionId, fetchQuestionSolution]);

  // Fetch solution when popup opens
  useEffect(() => {
    if (isOpen && questionId) {
      fetchSolution();
      setFlagSuccess(false); // Reset flag success when opening
    }
  }, [isOpen, questionId, fetchSolution]);

  // Handler for flagging the question
  const handleFlagQuestion = async () => {
    if (!flagReason.trim()) {
      alert('Please provide a reason for flagging this question');
      return;
    }

    setFlagging(true);
    try {
      const response = await flagQuestion(questionId, flagReason);
      if (response.status === 'success' || response.status === true) {
        setFlagSuccess(true);
        setShowFlagModal(false);
        setFlagReason('');
        // Show success message
        setTimeout(() => {
          setFlagSuccess(false);
        }, 5000);
      } else {
        alert(response.message || 'Failed to flag question');
      }
    } catch (error: any) {
      console.error('Error flagging question:', error);
      alert(error?.response?.data?.message || 'Failed to flag question. Please try again.');
    } finally {
      setFlagging(false);
    }
  };

  // Helper function to render content with selected renderer
  const renderMath = (content: string) => {
    if (useMathJax) {
      return <MathJaxRenderer>{content}</MathJaxRenderer>;
    } else {
      return <LaTeXRenderer>{content}</LaTeXRenderer>;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'danger';
      default: return 'secondary';
    }
  };

  const formatTime = (timeStr: string) => {
    // Handle different time formats
    if (!timeStr) return 'Unknown';
    return timeStr;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show d-block solution-popup solution-popup-overlay" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-lightbulb me-2"></i>
              Question Solution
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white close-button" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <div className="modal-body p-4">
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border loading-spinner" role="status">
                  <span className="visually-hidden">Loading solution...</span>
                </div>
                <p className="mt-3 text-muted">Loading solution...</p>
              </div>
            )}

            {error && (
              <div className="alert alert-warning text-center">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {solution && (
              <>
                {/* Question Image */}
                {questionImageUrl && (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0">
                        <i className="fas fa-image me-2"></i>
                        Question Image
                      </h6>
                    </div>
                    <div className="card-body text-center">
                      <img 
                        src={questionImageUrl.startsWith('http') 
                          ? questionImageUrl 
                          : questionImageUrl.startsWith('/') 
                            ? `${window.location.origin}${questionImageUrl}`
                            : `${window.location.origin}/api/${questionImageUrl}`
                        }
                        alt="Question"
                        className="img-fluid rounded border question-image"
                        style={{ 
                          maxHeight: '400px', 
                          maxWidth: '100%',
                          objectFit: 'contain',
                          backgroundColor: '#f8f9fa'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="bg-light p-4 rounded text-muted border border-dashed">
                                <i class="fas fa-image-slash fa-3x mb-2"></i>
                                <div class="small">Image not available</div>
                              </div>
                            `;
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Renderer Selection */}
                <div className="card mb-4 renderer-toggle">
                  <div className="card-body">
                    <h6 className="card-title">
                      <i className="fas fa-cogs me-2"></i>
                      View Options
                    </h6>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="mathRenderer"
                        id="mathJax"
                        value="mathjax"
                        checked={useMathJax}
                        onChange={() => setUseMathJax(true)}
                      />
                      <label className="form-check-label" htmlFor="mathJax">
                        <strong>View 1</strong> 
                        <small className="text-muted d-block">Better LaTeX handling</small>
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="mathRenderer"
                        id="katex"
                        value="katex"
                        checked={!useMathJax}
                        onChange={() => setUseMathJax(false)}
                      />
                      <label className="form-check-label" htmlFor="katex">
                        <strong>View 2</strong>
                        <small className="text-muted d-block">Faster rendering</small>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Solution Content */}
                <div className="row g-4">
                  {/* Solution Header Info */}
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row align-items-center mb-3">
                          <div className="col-md-8">
                            <h6 className="mb-2">
                              <strong>Question:</strong> {renderMath(solution.questionText)}
                            </h6>
                            <p className="text-muted mb-0">
                              <strong>Subject:</strong> {solution.subject} | <strong>Topic:</strong> {solution.topic}
                            </p>
                          </div>
                          <div className="col-md-4 text-end">
                            <span className={`badge bg-${getDifficultyColor(solution.difficulty)} fs-6 mb-2`}>
                              {solution.difficulty}
                            </span>
                            <br />
                            <small className="text-muted">
                              <i className="fas fa-clock me-1"></i>
                              Est. Time: {formatTime(solution.estimatedTimeToSolve)}
                            </small>
                          </div>
                        </div>

                        {/* Key Concepts */}
                        {solution.conceptsUsed && solution.conceptsUsed.length > 0 && (
                          <div className="mb-3">
                            <small className="fw-bold text-muted d-block mb-2">Key Concepts:</small>
                            <div className="d-flex flex-wrap gap-1">
                              {solution.conceptsUsed.map((concept, index) => (
                                <span key={index} className="concept-chip">
                                  {renderMath(concept)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Given Information */}
                        {solution.givenInformation && solution.givenInformation.length > 0 && (
                          <div>
                            <small className="fw-bold text-muted d-block mb-2">Given Information:</small>
                            <ul className="list-unstyled mb-0">
                              {solution.givenInformation.map((info, index) => (
                                <li key={index} className="mb-1">
                                  <i className="fas fa-info-circle text-info me-2"></i>
                                  {renderMath(info)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step-by-Step Solution */}
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">
                          <i className="fas fa-list-ol me-2"></i>
                          Step-by-Step Solution
                        </h6>
                      </div>
                      <div className="card-body">
                        {solution.solutionSteps?.map((step, index) => (
                          <div key={index} className="border rounded p-3 mb-3 bg-light solution-step-card">
                            <div className="d-flex align-items-start">
                              <span className="badge bg-primary me-3 mt-1">
                                Step {step.stepNumber}
                              </span>
                              <div className="flex-grow-1">
                                <h6 className="fw-bold mb-2">{step.title}</h6>
                                <p className="mb-2">{renderMath(step.explanation)}</p>
                                
                                {step.formula && (
                                  <div className="mb-2">
                                    <small className="fw-bold text-secondary">Formula:</small>
                                    <div className="bg-white p-2 rounded border">
                                      {renderMath(step.formula)}
                                    </div>
                                  </div>
                                )}
                                
                                {step.calculation && (
                                  <div className="mb-2">
                                    <small className="fw-bold text-secondary">Calculation:</small>
                                    <div className="bg-white p-2 rounded border">
                                      {Array.isArray(step.calculation) ? (
                                        <ul className="list-unstyled mb-0">
                                          {step.calculation.map((calc, calcIndex) => (
                                            <li key={calcIndex} className="mb-1">
                                              {renderMath(calc)}
                                            </li>
                                          ))}
                                        </ul>
                                      ) : (
                                        renderMath(step.calculation)
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                {step.result && (
                                  <div className="alert alert-info mb-0">
                                    <small className="fw-bold">Result:</small> {renderMath(step.result)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Final Answer */}
                  <div className="col-12">
                    <div className="card final-answer-card border-success">
                      <div className="card-header bg-success text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-check-circle me-2"></i>
                          Final Answer
                        </h6>
                      </div>
                      <div className="card-body bg-success bg-opacity-10">
                        <div className="fs-5 fw-bold text-center">
                          {renderMath(solution.finalAnswer)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Sections */}
                  {solution.keyInsights && solution.keyInsights.length > 0 && (
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header">
                          <h6 className="mb-0">
                            <i className="fas fa-key text-warning me-2"></i>
                            Key Insights
                          </h6>
                        </div>
                        <div className="card-body">
                          <ul className="list-unstyled mb-0">
                            {solution.keyInsights.map((insight, index) => (
                              <li key={index} className="mb-2 display-flex align-items-center">
                                {/* <i className="fas fa-lightbulb text-warning me-2"></i> */}
                                {renderMath(insight)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {solution.commonMistakes && solution.commonMistakes.length > 0 && (
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header">
                          <h6 className="mb-0">
                            <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                            Common Mistakes
                          </h6>
                        </div>
                        <div className="card-body">
                          <ul className="list-unstyled mb-0">
                            {solution.commonMistakes.map((mistake, index) => (
                              <li key={index} className="mb-2">
                                {/* <i className="fas fa-times-circle text-danger me-2"></i> */}
                                {renderMath(mistake)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Study Tips */}
                  {solution.studyTips && (
                    <div className="col-12">
                      <div className="card border-info">
                        <div className="card-header border-info">
                          <h6 className="mb-0">
                            <i className="fas fa-graduation-cap text-info me-2"></i>
                            Study Tips
                          </h6>
                        </div>
                        <div className="card-body">
                          <p className="mb-0">{renderMath(solution.studyTips)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Alternative Approaches */}
                  {solution.alternativeApproaches && solution.alternativeApproaches.length > 0 && (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="mb-0">
                            <i className="fas fa-route text-secondary me-2"></i>
                            Alternative Approaches
                          </h6>
                        </div>
                        <div className="card-body">
                          <ul className="mb-0">
                            {solution.alternativeApproaches.map((approach, index) => (
                              <li key={index} className="mb-2">
                                {renderMath(approach)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  
                  
                </div>
              </>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            {/* Flag Success Message */}
            {flagSuccess && (
              <div className="alert alert-success mb-0 me-auto" role="alert">
                <i className="fas fa-check-circle me-2"></i>
                Question flagged successfully! Thank you for your feedback.
              </div>
            )}
            
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              <i className="fas fa-times me-2"></i>
              Close
            </button>
            
            {solution && (
              <>
                <button 
                  type="button" 
                  className="btn btn-warning"
                  onClick={() => setShowFlagModal(true)}
                  disabled={flagSuccess}
                >
                  <i className="fas fa-flag me-2"></i>
                  Report Solution
                </button>
                
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={fetchSolution}
                  disabled={loading}
                >
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-refresh'} me-2`}></i>
                  Refresh Solution
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

      {/* Flag Question Modal */}
      {showFlagModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
            <div className="modal-header bg-warning text-dark">
              <h5 className="modal-title">
                <i className="fas fa-flag me-2"></i>
                Flag Question
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => {
                  setShowFlagModal(false);
                  setFlagReason('');
                }}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="mb-3">
                Please provide a reason for flagging this question. This helps us improve the quality of our content.
              </p>
              <div className="mb-3">
                <label htmlFor="flagReason" className="form-label">Reason for flagging:</label>
                <select 
                  className="form-select mb-2" 
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                >
                  <option value="">-- Select a reason --</option>
                  <option value="Wrong solution provided">Wrong solution provided</option>
                  <option value="No solution available">No solution available</option>
                  <option value="Incomplete solution">Incomplete solution</option>
                  <option value="Unclear explanation">Unclear explanation</option>
                  <option value="Wrong answer marked as correct">Wrong answer marked as correct</option>
                  <option value="Other">Other (specify below)</option>
                </select>
                {flagReason === 'Other' && (
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Please describe the issue..."
                    onChange={(e) => setFlagReason(`Other: ${e.target.value}`)}
                  ></textarea>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowFlagModal(false);
                  setFlagReason('');
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-warning" 
                onClick={handleFlagQuestion}
                disabled={flagging || !flagReason.trim()}
              >
                {flagging ? (
                  <>
                    <i className="fas fa-spinner fa-spin me-2"></i>
                    Flagging...
                  </>
                ) : (
                  <>
                    <i className="fas fa-flag me-2"></i>
                    Submit Flag
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default SolutionPopup;